export function calculateGrossByCategory(
  objects,
  monthName,
  year,
  category,
  quarter,
  half,
) {
  const monthMap = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  const parseGross = (gross) =>
    parseFloat((gross || "").toString().replace(/,/g, "")) || 0;

  if (category === "Monthly") {
    const targetMonth = monthMap[monthName];
    if (!targetMonth) throw new Error(`Invalid month name: "${monthName}"`);

    const daysInMonth = new Date(year, targetMonth, 0).getDate();

    const totals = Array(daysInMonth).fill(0);

    objects.forEach((obj) => {
      const objDate = new Date(obj.date);
      const objYear = objDate.getUTCFullYear();
      const objMonth = objDate.getUTCMonth() + 1;
      const objDay = objDate.getUTCDate();

      if (objYear === year && objMonth === targetMonth) {
        totals[objDay - 1] += parseGross(obj.gross);
      }
    });

    return totals;
  }

  if (category === "Quarterly") {
    if (!quarter) throw new Error(`"quarter" parameter required for Quarterly`);
    const quarterMap = {
      "1st Quarter": [1, 2, 3],
      "2nd Quarter": [4, 5, 6],
      "3rd Quarter": [7, 8, 9],
      "4th Quarter": [10, 11, 12],
    };
    const months = quarterMap[quarter];
    if (!months) throw new Error(`Invalid quarter: "${quarter}"`);

    const totals = Array(months.length).fill(0);

    objects.forEach((obj) => {
      const objDate = new Date(obj.date);
      const objYear = objDate.getUTCFullYear();
      const objMonth = objDate.getUTCMonth() + 1;

      if (objYear === year && months.includes(objMonth)) {
        const index = months.indexOf(objMonth);
        totals[index] += parseGross(obj.gross);
      }
    });

    return totals;
  }

  if (category === "Bi-Yearly") {
    if (!half) throw new Error(`"half" parameter required for Bi-Yearly`);
    let months;
    if (half === "1st Half") {
      months = [1, 2, 3, 4, 5, 6];
    } else if (half === "2nd Half") {
      months = [7, 8, 9, 10, 11, 12];
    } else {
      throw new Error(`Invalid half: "${half}". Use "H1" or "H2".`);
    }

    const totals = Array(months.length).fill(0);

    objects.forEach((obj) => {
      const objDate = new Date(obj.date);
      const objYear = objDate.getUTCFullYear();
      const objMonth = objDate.getUTCMonth() + 1;

      if (objYear === year && months.includes(objMonth)) {
        const index = months.indexOf(objMonth);
        totals[index] += parseGross(obj.gross);
      }
    });

    return totals;
  }

  if (category === "Yearly") {
    const totals = Array(12).fill(0);

    objects.forEach((obj) => {
      const objDate = new Date(obj.date);
      const objYear = objDate.getUTCFullYear();
      const objMonth = objDate.getUTCMonth() + 1;

      if (objYear === year) {
        totals[objMonth - 1] += parseGross(obj.gross);
      }
    });

    return totals;
  }

  throw new Error(
    `Invalid category: "${category}". Must be "Monthly", "Quarterly", "Bi-Yearly", or "Yearly".`,
  );
}

export function calculateNetProfitByCategory(
  receipts,
  sales,
  monthName,
  year,
  category,
  quarter,
  half,
) {
  const monthMap = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  const quarterMap = {
    "1st Quarter": [1, 2, 3],
    "2nd Quarter": [4, 5, 6],
    "3rd Quarter": [7, 8, 9],
    "4th Quarter": [10, 11, 12],
  };

  const halfMap = {
    "1st Half": [1, 2, 3, 4, 5, 6],
    "2nd Half": [7, 8, 9, 10, 11, 12],
  };

  const parseGross = (gross) =>
    parseFloat((gross || "").toString().replace(/,/g, "")) || 0;

  const getGroupingInfo = () => {
    switch (category) {
      case "Monthly": {
        const month = monthMap[monthName];
        if (!month) throw new Error(`Invalid month name: "${monthName}"`);
        const days = new Date(year, month, 0).getDate();
        return {
          length: days,
          getIndex: (date) =>
            date.getUTCMonth() + 1 === month ? date.getUTCDate() - 1 : -1,
        };
      }
      case "Quarterly": {
        if (!quarter || !quarterMap[quarter])
          throw new Error(`"quarter" parameter required for Quarterly`);
        const months = quarterMap[quarter];
        return {
          length: 3,
          getIndex: (date) => {
            const m = date.getUTCMonth() + 1;
            const i = months.indexOf(m);
            return i !== -1 ? i : -1;
          },
        };
      }
      case "Bi-Yearly": {
        if (!half || !halfMap[half])
          throw new Error(`"half" parameter required for Bi-Yearly`);
        const months = halfMap[half];
        return {
          length: 6,
          getIndex: (date) => {
            const m = date.getUTCMonth() + 1;
            const i = months.indexOf(m);
            return i !== -1 ? i : -1;
          },
        };
      }
      case "Yearly": {
        return {
          length: 12,
          getIndex: (date) => date.getUTCMonth(),
        };
      }
      default:
        throw new Error(
          `Invalid category: "${category}". Must be "Monthly", "Quarterly", "Bi-Yearly", or "Yearly".`,
        );
    }
  };

  const calculateNetProfit = () => {
    const { length, getIndex } = getGroupingInfo();
    const salesTotals = Array(length).fill(0);
    const receiptsTotals = Array(length).fill(0);

    const processEntries = (entries, targetArray) => {
      for (const obj of entries) {
        const date = new Date(obj.date);
        if (date.getUTCFullYear() !== year) continue;
        const index = getIndex(date);
        if (index !== -1) {
          targetArray[index] += parseGross(obj.gross);
        }
      }
    };

    processEntries(sales, salesTotals);
    processEntries(receipts, receiptsTotals);

    return salesTotals.map((sale, i) => Math.max(sale - receiptsTotals[i], 0));
  };

  return calculateNetProfit();
}

export function getCategories(monthName, year, quarter, half, category) {
  const allMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthMap = {
    January: 1,
    February: 2,
    March: 3,
    April: 4,
    May: 5,
    June: 6,
    July: 7,
    August: 8,
    September: 9,
    October: 10,
    November: 11,
    December: 12,
  };

  if (category === "Monthly") {
    const targetMonth = monthMap[monthName];
    if (!targetMonth) return [];

    const daysInMonth = new Date(year, targetMonth, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString());
  }

  if (category === "Quarterly") {
    if (!quarter) return [];
    const quarterMap = {
      "1st Quarter": ["January", "February", "March"],
      "2nd Quarter": ["April", "May", "June"],
      "3rd Quarter": ["July", "August", "September"],
      "4th Quarter": ["October", "November", "December"],
    };
    return quarterMap[quarter] || [];
  }

  if (category === "Bi-Yearly") {
    if (!half) return [];
    const halves = {
      "1st Half": ["January", "February", "March", "April", "May", "June"],
      "2nd Half": [
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
    };
    return halves[half] || [];
  }

  if (category === "Yearly") {
    return allMonths;
  }

  return [];
}
